import React, { useState } from 'react';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { ViewStateType } from 'components/ProductTemplates/types';
import { useDeleteCheckoutDotComPaymentCard } from 'views/Accounts/hooks/useDeleteCheckoutDotComPaymentCard';
import { useUpdateDefaultCheckoutDotComCard } from 'views/Accounts/hooks/useUpdateDefaultCheckoutDotComCard';
import { logError } from 'components/LogError';
import { cache } from 'graphql/cache';
import { CardType } from 'types/commonTypes';
import { DeleteIcon } from 'assets/icons';
import { Button } from 'components';
import { selectImage } from '../Checkout.Com/Card';

interface EditCheckoutDotComCardProps {
  cards: (CardType | null)[];
  onClose: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

interface ISelectedCards extends CardType {
  actionType: ActionType;
  message: string;
  action: () => void;
}
enum ActionType {
  edit = 'edit',
  delete = 'delete',
  setDefault = 'setDefault',
  none = 'none',
}

export default function EditCardHomePage({ cards, onClose, onError, onSuccess }: EditCheckoutDotComCardProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const { updateDefaultCard } = useUpdateDefaultCheckoutDotComCard();
  const { deleteCard } = useDeleteCheckoutDotComPaymentCard();
  const [selectedCards, setSelectedCard] = useState<ISelectedCards | null>(null);

  async function attemptToUpdateDefaultCard(card: CardType | null) {
    if (!card?.id) {
      return;
    }

    setIsLoading(true);
    try {
      const { data: updateDefaultCardResponse, errors: updateDefaultCardError } = await updateDefaultCard({
        variables: {
          defaultMitCard: {
            instrumentId: card.id,
          },
        },
      });
      if (updateDefaultCardResponse?.updateDefaultMitCard.errorMessage) {
        throw new Error(updateDefaultCardResponse?.updateDefaultMitCard.errorMessage);
      }
      if (updateDefaultCardError) {
        throw updateDefaultCardError[0];
      }
      cache.evict({ fieldName: 'paymentGetCardDetails' });
      onSuccess('Card set successfully');
    } catch (e) {
      logError(e as Error);
    } finally {
      onError('Error on setting card default');
      setIsLoading(false);
      onClose();
    }
  }

  async function handleConfirmDeleteCardClicked(card: CardType | null) {
    if (!card?.id) {
      return;
    }

    setIsLoading(true);
    try {
      const { success } = await deleteCard(card.id);
      if (success) {
        cache.evict({ fieldName: 'paymentGetCardDetails' });
        onSuccess('Card deleted successfully');
      } else {
        onError('Error on setting card default');
      }
    } catch (e) {
      setIsLoading(false);
      onError('Error on setting card default');
    } finally {
      setIsLoading(false);
      onClose();
    }
  }

  const onActions = (actionType: ActionType, cardInfo: CardType | null) => {
    if (actionType === ActionType.none) {
      setSelectedCard(null);
      return;
    }

    let values = {
      actions: () => {},
      message: '',
    };

    switch (actionType) {
      case ActionType.delete:
        values = {
          actions: () => handleConfirmDeleteCardClicked(cardInfo),
          message: 'Are you sure you want to delete.',
        };
        break;

      case ActionType.setDefault:
        values = {
          actions: () => attemptToUpdateDefaultCard(cardInfo),
          message: 'Are you sure you want to set card as default.',
        };
        break;

      default:
        break;
    }

    setSelectedCard({ ...cardInfo, actionType, message: values.message, action: values.actions });
  };

  return (
    <div className="flex flex-col flex-1 bg-gradient-to-b from-gray-100 to-gray-500 dark:from-slate-800 dark:to-slate-900 pb-5 px-3 w-full relative overflow-x-hidden sm:w-[300px]">
      <div className="w-full h-full rounded-md  overflow-y-auto flex flex-col">
        <FeedbackTemplate onClose={onClose} onCTA={() => {}} viewState={ViewStateType.DEFAULT}>
          <div className="flex flex-col bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700 h-full w-full p-4 gap-4 ">
            {cards.map((x, index) => {
              return (
                <div
                  className={`rounded-xl flex flex-row ${
                    x?.defaultMitFlag ? 'bg-[#A1A3F999] dark:bg-[#A1A3F940]' : 'bg-gray-200 dark:bg-slate-700'
                  } p-3 relative min-h-[100px]`}
                >
                  {selectedCards?.id !== x?.id && (
                    <div className="flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-1">
                        {selectImage(x?.scheme)}
                        <div className="flex flex-row gap-2">
                          <DeleteIcon
                            className="w-[13px] h-[13px] cursor-pointer"
                            onClick={() => {
                              if (isLoading) return;
                              onActions(ActionType.delete, x);
                            }}
                          />
                        </div>
                      </div>
                      {x?.defaultMitFlag && (
                        <span className="text-sm dark:text-white">{'Default Card'.toUpperCase()}</span>
                      )}
                      <span className="text-sm dark:text-white">{x?.name}</span>
                      <span className="text-sm dark:text-gray-300">{`xxxx-xxxx-xxxx-${x?.last4}`}</span>
                      <span className="text-xs dark:text-gray-400">{x?.expiryDate}</span>

                      <label
                        className="flex items-center gap-2 text-sm dark:text-gray-300 mt-1 cursor-pointer"
                        htmlFor={`card-${index}`}
                      >
                        Set as default
                        <input
                          disabled={isLoading || x?.defaultMitFlag === true}
                          id={`card-${index}`}
                          name={`card-${index}`}
                          type="checkbox"
                          checked={x?.defaultMitFlag || false}
                          onClick={() => {
                            onActions(ActionType.setDefault, x);
                          }}
                          className="appearance-none w-4 h-4 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-orange dark:checked:bg-teal-500 checked:border-orange dark:checked:border-teal-500 focus:ring-2 focus:ring-orange/30 dark:focus:ring-teal-500/30 focus:ring-offset-0 disabled:opacity-50 transition-all cursor-pointer"
                        />
                      </label>
                    </div>
                  )}

                  {selectedCards?.id === x?.id && (
                    <div className="flex  flex-1 flex-col items-center text-sm dark:text-white gap-3">
                      <span className="text-center">{selectedCards?.message}</span>
                      <div className="flex w-full flex-1 items-center justify-center gap-2">
                        <Button
                          isDisable={isLoading}
                          isProcessing={isLoading}
                          className="btn bg-orange dark:bg-teal-500"
                          onClick={selectedCards?.action}
                        >
                          Comfirm
                        </Button>
                        <Button
                          isDisable={isLoading}
                          className="btn border border-black dark:border-slate-500 dark:text-gray-300"
                          onClick={() => setSelectedCard(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </FeedbackTemplate>
      </div>
    </div>
  );
}
